import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import { ProfileAnchor } from '@databox/types/domains/settings/settings.enums';

import type { Service } from 'webapp-types';
import { SourceTypeCategory } from 'webapp/enums/enums';
import { sortBy } from 'webapp/utils/common';

import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type SourceType from 'webapp/models-custom/source-type';

interface ConnectPanelItem {
	order: number;
	title: string;
	icon?: IconName;
	imgSrc?: string;
	description?: string;
	showLearnMore?: boolean;
	isHidden?: boolean;
	hasNewTag?: boolean;
	hasBetaTag?: boolean;
	sourceTypeObj?: SourceType;
	collection?: SourceType[];
	action?: {
		text: string;
		isLinkExternal?: boolean;
		fn?: (...args: unknown[]) => void;
		link?: string;
		learnMore?: string;
		isHidden?: boolean;
	};
}

interface Args {
	model: SourceType[];
	preventAppendingBodyClass?: boolean;
	isOnboarding?: boolean;
	onConnectOrCreateToken?: (model: SourceType) => void;
}

export default class DataManagerConnectPanelComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('navigation') declare navigationService: Service.Navigation;

	items: ConnectPanelItem[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (!this.args.preventAppendingBodyClass) {
			schedule('afterRender', this, () => {
				document.body.classList.add('has-right-panel');
			});
		}

		const model = this.args.model,
			workflowSources = model.filter((item) => item.category === SourceTypeCategory.WORKFLOW),
			companyName = this.accountService.shownCompanyName;
		const hideApiIntegration =
			this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins;

		this.items = sortBy(
			[
				{
					order: 1,
					imgSrc: 'https://cdn1.databox.com/images/apps/s/IngestionAPI.png',
					title: 'API',
					description: `Ingest raw, structured data into ${companyName} in table format using our REST API. Ideal for sending unprocessed data for storage, transformation, and analysis.`,
					hasNewTag: true,
					action: {
						text: 'Create API key',
						fn: this.redirectToProfile,
						isHidden: this.args.isOnboarding,
						learnMore: 'https://developers.databox.com/api/overview',
					},
					isHidden: hideApiIntegration,
				},
				{
					order: 2,
					icon: 'Api',
					title: 'Build your own integration',
					description: `Create datasets and pull data from any REST API so you can build metrics and do deeper analysis.`,
					action: {
						text: 'New integration',
						fn: this.startCustomIntegrationConnectFlow,
						isHidden: this.args.isOnboarding,
						learnMore: 'https://help.databox.com/set-up-a-custom-integration',
					},
					isHidden: this.args.isOnboarding,
				},
				{
					order: 3,
					icon: 'DataInsert',
					title: 'Data pipeline partners',
					description: `Use ETL / ELT tools to automatically move and prepare data for ${companyName}.`,
					action: {
						text: 'Dataddo',
						isLinkExternal: true,
						fn: this.redirectToDataddo,
						isHidden: this.args.isOnboarding,
					},
				},
				{
					order: 4,
					icon: 'Bolt',
					title: 'Automation & workflow tools',
					description: `Use an event / trigger-based app to send data to ${companyName}.`,
					collection: workflowSources,
				},
				{
					order: this.args.isOnboarding ? 2 : 5,
					icon: 'Wrench',
					title: 'Custom integration services',
					description:
						"Need some help? We're here for you. Fill out the questionnaire below and we'll be in touch with next steps",
					isHidden: !this.aiChatService.hasSupportChat,
					action: {
						link: 'https://meetings.hubspot.com/databox-meeting/custom-integration-evaluation-call-in-app',
						text: 'Request assistance',
						isLinkExternal: true,
					},
				},
			],
			'order',
		);
	}

	@action
	connectOrCreateToken(model: SourceType) {
		if (this.args.onConnectOrCreateToken) {
			this.args.onConnectOrCreateToken(model);
		} else {
			this.mmSourceTypesService.goToSourceTypeInfo(model);
		}
	}

	@action
	redirectToProfile() {
		this.aiChatService.onNavigateToProfile(ProfileAnchor.API_KEY);
	}

	@action
	redirectToDataddo() {
		window.open('https://www.dataddo.com/storage/databox', '_blank');
	}

	@action
	startCustomIntegrationConnectFlow() {
		const customIntegrations = this.args.model.find(
			(item) => item.key === 'CustomIntegrations',
		);

		if (customIntegrations) {
			this.connectOrCreateToken(customIntegrations);
		}
	}

	@action
	teardownView(): void {
		document.body.classList.remove('has-right-panel');
	}
	<template>
		<div id="data-manager-connect-panel" {{willDestroy this.teardownView}}>
			{{#unless @isOnboarding}}
				<div class="topline">
					Other ways to bring in data
				</div>
			{{/unless}}
			<div class="wrapper">
				{{! for white background on the right over subnavi }}
				<div class="ct">
					<ul class="additional-ways">
						{{#each this.items as |item|}}
							{{#unless item.isHidden}}
								<li>
									<div class="image">
										{{#if item.icon}}
											<CommonUiIconComponent @name={{item.icon}} @size="lg" />
										{{else}}
											<img src={{item.imgSrc}} alt />
										{{/if}}
									</div>
									<div class="info">
										<span class="label">
											{{item.title}}
											{{#unless @isOnboarding}}
												{{#if item.hasNewTag}}
													<div class="new-feature-tag">New</div>
												{{/if}}
												{{#if item.hasBetaTag}}
													<div class="beta-tag">Beta</div>
												{{/if}}
											{{/unless}}
										</span>
										{{#if item.description}}
											<p class="description">{{item.description}}</p>
										{{/if}}

										{{#unless @isOnboarding}}
											{{#if item.collection}}
												<ul class="collection">
													{{#each item.collection as |collectionItem|}}
														<li class="collection-item">
															<a
																class="blue-link"
																role="button"
																{{on
																	"click"
																	(fn
																		this.connectOrCreateToken
																		collectionItem
																	)
																}}
															>
																{{collectionItem.name}}
															</a>
															{{#if item.showLearnMore}}
																{{#if
																	collectionItem.connectLinkWithFallback
																}}
																	<a
																		class="learn-more-link"
																		href={{collectionItem.connectLinkWithFallback}}
																		target="_blank"
																	>
																		Learn more
																	</a>
																{{/if}}
															{{/if}}
														</li>
													{{/each}}
												</ul>
											{{/if}}
										{{/unless}}

										{{#if item.action}}
											<div class="actions">
												{{#unless item.action.isHidden}}
													{{#if item.action.link}}
														<a
															class="blue-link"
															target="_blank"
															href={{item.action.link}}
														>
															{{item.action.text}}
															{{#if item.action.isLinkExternal}}
																<CommonUiIconComponent
																	@name="External"
																	@size="xs"
																/>
															{{/if}}
														</a>
													{{else if item.action.fn}}
														<a
															class="blue-link"
															role="button"
															{{on
																"click"
																(fn
																	item.action.fn
																	item.sourceTypeObj
																)
															}}
														>
															{{item.action.text}}
															{{#if item.action.isLinkExternal}}
																<CommonUiIconComponent
																	@name="External"
																	@size="xs"
																/>
															{{/if}}
														</a>
													{{/if}}
												{{/unless}}

												{{#unless
													this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins
												}}
													{{#if
														item.sourceTypeObj.connectLinkWithFallback
													}}
														<a
															class="learn-more-link"
															href={{item.sourceTypeObj.connectLinkWithFallback}}
															target="_blank"
														>
															Learn more
															<CommonUiIconComponent
																@name="External"
																@size="xs"
															/>
														</a>
													{{else if item.action.learnMore}}
														<a
															class="learn-more-link"
															href={{item.action.learnMore}}
															target="_blank"
														>
															Learn more
															<CommonUiIconComponent
																@name="External"
																@size="xs"
															/>
														</a>
													{{/if}}
												{{/unless}}
											</div>
										{{/if}}
									</div>
								</li>
							{{/unless}}
						{{/each}}
					</ul>
				</div>
			</div>
		</div>
	</template>
}
